import React, { useState, useCallback, useEffect } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import type { EmblaCarouselType } from 'embla-carousel';

import { Tab as TabProps } from '../../index';

import Image from 'next/image';
import defaultProfileImage from 'Public/images/cards/profile/default.png';

import {
    EmblaCarousel,
    EmblaContainer,
    Slide,
    Controls,
    PrevNextWrapper,
    PrevButton,
    NextButton,
    PrevIcon,
    NextIcon,
    Pagination,
    PaginationButton
} from './AvatarMobile.styled';

import {
    Tab,
    Label,
    AvatarWrapper,
    AvatarContainer,
    BioContainer,
    Position,
    Organisation
} from '../../Avatar.styled';

export interface Props {
    tabs: TabProps[];
    activeTab: (index: number) => void;
}

const AvatarMobile: React.FC<Props> = ({ tabs, activeTab }) => {
    const [emblaRef, emblaApi] = useEmblaCarousel({
        loop: true,
        align: 'start',
        inViewThreshold: 0.8
    });
    const [slidesInView, setSlidesInView] = useState<number[]>([]);
    const [activeTabIndex, setActiveTabIndex] = useState(0);

    const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
    const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
    const scrollTo = useCallback(
        (index: number) => emblaApi && emblaApi.scrollTo(index),
        [emblaApi]
    );

    const onSlidesInView = useCallback((emblaApi: EmblaCarouselType) => {
        setSlidesInView(emblaApi.slidesInView());
    }, []);

    useEffect(() => {
        if (!emblaApi) return;

        onSlidesInView(emblaApi);
        emblaApi.on('slidesInView', onSlidesInView);
    }, [emblaApi, onSlidesInView]);

    const renderTab = (tab: TabProps, index: number): React.ReactNode => {
        return (
            <Tab
                id={`tab-${tab.id}`}
                key={`tab-${tab.id}`}
                role="tab"
                aria-label={`Click for more information about ${tab.name}, ${tab.position}`}
                aria-controls={`panel-${tab.id}`}
                aria-selected={index === activeTabIndex}
                tabIndex={-1}
                onClick={() => {
                    setActiveTabIndex(index);
                    activeTab(index);
                }}
                $isActive={index === activeTabIndex}
            >
                <AvatarContainer>
                    <AvatarWrapper>
                        <Image
                            src={tab.avatar?.src || defaultProfileImage}
                            alt=""
                            fill
                            style={{ objectFit: 'cover' }}
                        />
                    </AvatarWrapper>
                    <BioContainer>
                        {tab.name && <Label $isActive={index === activeTabIndex}>{tab.name}</Label>}
                        <Position>{tab.position}</Position>
                        <Organisation>{tab.organisation}</Organisation>
                    </BioContainer>
                </AvatarContainer>
            </Tab>
        );
    };

    const mobileTabNav = () => {
        const content: React.ReactElement<any>[] = [];
        for (let index = 0; index < tabs.length; index = index + 2) {
            const tabOne = tabs[index];
            //ternary below to account for potentially odd number of cards
            const tabTwo = tabs[index + 1] ? tabs[index + 1] : undefined;

            content.push(
                <Slide key={`tab-mobile-${tabs[index].id}`}>
                    {renderTab(tabOne, index)}
                    {tabTwo && renderTab(tabTwo, index + 1)}
                </Slide>
            );
        }
        return content;
    };

    return (
        <div role="tablist" aria-label="Graduate stories tabs">
            <EmblaCarousel ref={emblaRef}>
                <EmblaContainer>{mobileTabNav()}</EmblaContainer>
            </EmblaCarousel>
            <Controls>
                <PrevNextWrapper>
                    <PrevButton aria-label="Previous slide" onClick={scrollPrev}>
                        <PrevIcon />
                    </PrevButton>
                    <NextButton aria-label="Next slide" onClick={scrollNext}>
                        <NextIcon />
                    </NextButton>
                </PrevNextWrapper>
                <nav aria-label={`mobile pagination nav`}>
                    <Pagination>
                        {mobileTabNav().map((item, index) => (
                            <li key={`pagination-${index}-${item.props.id}`}>
                                <PaginationButton
                                    aria-label={`Go to slide ${index + 1}`}
                                    aria-current={slidesInView.includes(index)}
                                    $inView={slidesInView.includes(index)}
                                    onClick={() => scrollTo(index)}
                                />
                            </li>
                        ))}
                    </Pagination>
                </nav>
            </Controls>
        </div>
    );
};

export default AvatarMobile;
